import React, { useState, useEffect } from 'react';
import { auditApi } from '../services/api';
import { AuditEntry } from '../types';
import './AuditHistoryModal.css';

interface AuditHistoryModalProps {
  tableName: string;
  recordId: number;
  recordLabel: string;
  fieldLabels: Record<string, string>;
  onClose: () => void;
}

const AuditHistoryModal: React.FC<AuditHistoryModalProps> = ({
  tableName,
  recordId,
  recordLabel,
  fieldLabels,
  onClose,
}) => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        setLoading(true);
        const response = await auditApi.getHistory(tableName, recordId);
        setEntries(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Erreur chargement historique audit:', err);
      } finally {
        setLoading(false);
      }
    };
    loadHistory();
  }, [tableName, recordId]);

  const getFieldLabel = (key: string): string => {
    return fieldLabels[key] || key;
  };

  const formatDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    } catch {
      return dateStr;
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '(vide)';
    return String(value);
  };

  const getOperationLabel = (op: string): string => {
    switch (op) {
      case 'INSERT': return 'Création';
      case 'UPDATE': return 'Modification';
      case 'DELETE': return 'Suppression';
      default: return op;
    }
  };

  const renderChanges = (entry: AuditEntry) => {
    if (entry.operation === 'UPDATE' && entry.old_values && entry.new_values) {
      const allKeys = new Set([
        ...Object.keys(entry.old_values),
        ...Object.keys(entry.new_values),
      ]);
      const changedKeys = Array.from(allKeys).filter(
        (key) => formatValue(entry.old_values![key]) !== formatValue(entry.new_values![key])
      );

      if (changedKeys.length === 0) {
        return <div className="audit-changes"><em>Aucun changement détecté</em></div>;
      }

      return (
        <div className="audit-changes">
          {changedKeys.map((key) => (
            <div key={key} className="audit-change-row">
              <span className="audit-field-name">{getFieldLabel(key)}</span>
              <span className="audit-old-value">{formatValue(entry.old_values![key])}</span>
              <span className="audit-arrow">&rarr;</span>
              <span className="audit-new-value">{formatValue(entry.new_values![key])}</span>
            </div>
          ))}
        </div>
      );
    }

    if (entry.operation === 'INSERT' && entry.new_values) {
      return (
        <div className="audit-changes">
          {Object.entries(entry.new_values).map(([key, value]) => (
            <div key={key} className="audit-change-row">
              <span className="audit-field-name">{getFieldLabel(key)}</span>
              <span className="audit-value">{formatValue(value)}</span>
            </div>
          ))}
        </div>
      );
    }

    if (entry.operation === 'DELETE' && entry.old_values) {
      return (
        <div className="audit-changes">
          {Object.entries(entry.old_values).map(([key, value]) => (
            <div key={key} className="audit-change-row">
              <span className="audit-field-name">{getFieldLabel(key)}</span>
              <span className="audit-old-value">{formatValue(value)}</span>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="audit-modal-overlay" onClick={onClose}>
      <div className="audit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="audit-modal-header">
          <h2>Historique - {recordLabel}</h2>
          <button className="audit-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="audit-modal-body">
          {loading ? (
            <div className="audit-loading">Chargement de l'historique...</div>
          ) : entries.length === 0 ? (
            <div className="audit-empty">Aucun historique disponible</div>
          ) : (
            <div className="audit-timeline">
              {entries.map((entry) => (
                <div key={entry.id} className={`audit-entry op-${entry.operation}`}>
                  <div className="audit-entry-header">
                    <span className={`audit-badge op-${entry.operation}`}>
                      {getOperationLabel(entry.operation)}
                    </span>
                    <div className="audit-meta">
                      <span>{formatDate(entry.created_at)}</span>
                      <span>par {entry.user_name}</span>
                    </div>
                  </div>
                  {renderChanges(entry)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditHistoryModal;
